/* Checkbox — matches Figma Checkbox (node 1101:539). Single 14×14
 * Small/Square variant; Figma reserves size/shape enums for future
 * expansion but only this variant ships today.
 *
 * DOM structure:
 *   <label> wrapper (flex row: [input | box | label-text])
 *     <input type=checkbox>  ← sr-only, native click+keyboard target
 *     <span .box>             ← 14×14 visible box, sibling selectors
 *       <svg check | minus>   ← glyph when checked / indeterminate
 *     </span>
 *     <span .label>Text</span> (optional)
 *   </label>
 *
 * The input sits visually behind/hidden but remains the real
 * interactive target. Sibling combinators (.input + .box) let CSS
 * paint the visual state without JS mirroring. */

.wrapper {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-8);
  padding: 2px;
  cursor: pointer;
  user-select: none;
}

.wrapper:has(.input:disabled) {
  cursor: not-allowed;
}

/* sr-only pattern — input is visually invisible (opacity:0) but
 * still receives clicks and keyboard focus. `position: absolute`
 * without explicit top/left offsets places the input at its
 * static position (left edge of the flex container — the same
 * spot .box occupies once the input is taken out of flow), so
 * they overlap. `.box` has pointer-events: none, so clicks on
 * the visible box area fall through to the input below. */
.input {
  position: absolute;
  width: 14px;
  height: 14px;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: inherit;
}

/* ─── Box (visible 14×14 control) ──────────────────────────────── */

.box {
  width: 14px;
  height: 14px;
  border: 1px solid var(--stroke-neutral);
  border-radius: var(--radius-4);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  /* Icon color when glyph is visible (checked / indeterminate).
   * Always white on the accent fill per Figma spec. */
  color: #ffffff;
  /* Let clicks pass through to the sr-only input beneath. */
  pointer-events: none;
  transition:
    background var(--motion-duration-fast),
    border-color var(--motion-duration-fast),
    box-shadow var(--motion-duration-fast);
}

/* Hover — only when the wrapper is hovered AND the input is not
 * disabled. Teal-600 border (light) / teal-300 (dark) — matches
 * `--stroke-input-active` from tokens-figma.css. No token yet for
 * this specific hover tint — see design-asks for `--checkbox-accent`. */
.wrapper:hover .input:not(:disabled):not(:checked):not(:indeterminate) + .box {
  border-color: #0d9488;
}

[data-theme="dark"] .wrapper:hover .input:not(:disabled):not(:checked):not(:indeterminate) + .box {
  border-color: #5eead4;
}

/* Checked / indeterminate — accent fill. Teal literals same source
 * as hover. */
.input:checked + .box,
.input:indeterminate + .box {
  background: #0d9488;
  border-color: #0d9488;
}

[data-theme="dark"] .input:checked + .box,
[data-theme="dark"] .input:indeterminate + .box {
  background: #5eead4;
  border-color: #5eead4;
}

/* Focus-visible — 2px outer ring. Neutral tint when unchecked, teal
 * tint when checked/indeterminate. Light and dark share the same
 * rgba values (20% alpha teal / 80% alpha neutral-light / 20% alpha
 * neutral-dark). */
.input:focus-visible + .box {
  box-shadow: 0 0 0 2px rgba(229, 229, 229, 0.8);
}

[data-theme="dark"] .input:focus-visible + .box {
  box-shadow: 0 0 0 2px rgba(64, 64, 64, 0.2);
}

.input:focus-visible:checked + .box,
.input:focus-visible:indeterminate + .box {
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.2);
}

/* Disabled — unchecked is the default box with lowered opacity.
 * Checked/indeterminate disabled uses neutral-400 fill (--icon-muted
 * maps to #a3a3a3 in both modes per tokens-figma.css). */
.input:disabled + .box {
  opacity: 0.6;
}

.input:disabled:checked + .box,
.input:disabled:indeterminate + .box {
  background: var(--icon-muted);
  border-color: var(--icon-muted);
  opacity: 1;
}

/* ─── Label text ───────────────────────────────────────────────── */

.label {
  /* Body/Micro per the Figma reference; --text-xs-tracking matches
   * Chip's label treatment. */
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-primary);
}

.wrapper:has(.input:disabled) .label {
  color: var(--text-tertiary);
}
